import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Music2, Mail, MessageCircle, Lock } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { useApp } from "@/context/AppContext";
import { CONTACT } from "@/data/siteConfig";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const { t } = useApp();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      toast.success(res.data.message || t.newsletter.success);
      setEmail("");
    } catch (err) {
      toast.error("Bitte gültige E-Mail eingeben.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#21160B] border-t border-white/5 pt-24 pb-10">
      {/* Newsletter */}
      <section data-testid="newsletter-section" className="max-w-[1440px] mx-auto px-6 md:px-10 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center border-b border-white/5 pb-20">
          <div>
            <div className="kicker kicker-eyebrow mb-4">{t.newsletter.kicker}</div>
            <h3 className="font-heading text-4xl md:text-5xl tracking-tight text-white">{t.newsletter.title}</h3>
            <p className="mt-4 text-white/60 max-w-md">{t.newsletter.subtitle}</p>
          </div>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4">
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.newsletter.placeholder}
              className="flex-1 bg-transparent border-b border-white/25 focus:border-[#C9A36B] py-4 px-2 text-white placeholder-white/40 outline-none transition-colors"
            />
            <button
              data-testid="newsletter-submit-button"
              type="submit"
              disabled={loading}
              className="btn-champagne disabled:opacity-60"
            >{loading ? "..." : t.newsletter.cta}</button>
          </form>
        </div>
      </section>

      {/* Footer grid */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2 md:col-span-2">
          <LogoFull size={64} />
          <p className="mt-6 text-white/50 max-w-xs kicker">{t.footer.tagline}</p>
          <div className="flex gap-3 mt-8">
            <a href={CONTACT.instagramHref} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="icon-btn text-white/70"><Instagram size={17} strokeWidth={1.4} /></a>
            <a href={CONTACT.tiktokHref} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="social-tiktok" className="icon-btn text-white/70"><Music2 size={17} strokeWidth={1.4} /></a>
            <a href={CONTACT.emailHref} aria-label="E-Mail" data-testid="footer-email" className="icon-btn text-white/70"><Mail size={17} strokeWidth={1.4} /></a>
            <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid="footer-whatsapp" className="icon-btn text-white/70"><MessageCircle size={17} strokeWidth={1.4} /></a>
          </div>
          <div className="mt-5 space-y-1.5">
            <a href={CONTACT.emailHref} data-testid="footer-email-text" className="block text-white/50 text-sm hover:text-[#C9A36B] transition-colors">{CONTACT.email}</a>
            <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-text" className="block text-white/50 text-sm hover:text-[#C9A36B] transition-colors">{CONTACT.whatsappDisplay} (WhatsApp)</a>
          </div>
        </div>

        <FooterCol title={t.footer.shop} links={[
          { label: t.footer.links.collection, to: "/collection" },
          { label: t.footer.links.configurator, to: "/configurator" },
        ]} />

        <FooterCol title={t.footer.brand} links={[
          { label: t.nav.home, to: "/" },
          { label: t.footer.links.about, to: "/about" },
        ]} />

        <FooterCol title={t.footer.support} links={[
          { label: t.footer.links.contact, to: "/contact" },
        ]} />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="text-xs text-white/40 font-mono tracking-widest">{t.footer.copyright}</div>
        <div className="flex gap-6 text-xs text-white/40 font-mono tracking-widest">
          <a href="#" className="hover:text-[#C9A36B] transition-colors">{t.footer.links.imprint}</a>
          <a href="#" className="hover:text-[#C9A36B] transition-colors">{t.footer.links.privacy}</a>
          <a href="#" className="hover:text-[#C9A36B] transition-colors">{t.footer.links.terms}</a>
          <Link
            to="/admin/saved"
            data-testid="footer-admin-link"
            className="inline-flex items-center gap-1.5 hover:text-[#C9A36B] transition-colors"
          >
            <Lock size={11} strokeWidth={1.6} /> Admin
          </Link>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <div className="font-mono text-[0.65rem] tracking-[0.3em] uppercase text-[#C9A36B] mb-5">{title}</div>
    <ul className="space-y-3">
      {links.map((l, i) => (
        <li key={i}>
          <Link to={l.to} className="text-white/70 text-sm hover:text-[#C9A36B] transition-colors">{l.label}</Link>
        </li>
      ))}
    </ul>
  </div>
);
